/**
 * Pure enrichment of the worker-produced DiagramSpec with frontend-only
 * data: live metric snapshots/history for metric nodes, and value/handler
 * wiring for simInput nodes. Extracted from DiagramWorkspace so the
 * pass-through contract (spec-level fields like `appearance` survive
 * enrichment) is unit-testable without mounting the workspace.
 */
import type { DiagramSpec, DiagramNode } from "@diagram/diagram-view";
import type { InputDescriptor } from "@diagram/sim-model";
import { formatMetricValue } from "@diagram/sim-model";
import type { TaggedMetricSnapshot } from "@diagram/sim-worker";
import type { MetricTimeSeries } from "../hooks/useMetricHistory";

/**
 * Merge a registered input descriptor (min/max/step/kind/defaultValue),
 * the frontend-owned current value, and the change handler into a `simInput`
 * diagram node's `data`.
 *
 * The slider is fully controlled by `value`, which comes from the frontend's
 * `inputValues` map. `useInputs` seeds the map from `descriptor.defaultValue`
 * as inputs are registered, so the descriptor default is only used here as a
 * safety net for ids not yet present in the map.
 */
function enrichInputNode(
  node: DiagramNode,
  descriptor: InputDescriptor | undefined,
  inputValues: Record<string, number | boolean>,
  setInputValue: (id: string, value: number | boolean) => void,
): DiagramNode {
  // inputValues is authoritative; descriptor.defaultValue is the fallback for
  // ids not yet in the map.
  const value: number | boolean | undefined =
    Object.prototype.hasOwnProperty.call(inputValues, node.id)
      ? inputValues[node.id]
      : descriptor?.defaultValue;
  return {
    ...node,
    data: {
      ...node.data,
      ...(descriptor && {
        inputKind: descriptor.kind,
        min: descriptor.min,
        max: descriptor.max,
        step: descriptor.step,
        defaultValue: descriptor.defaultValue,
      }),
      ...(value !== undefined && { value }),
      onValueChange: setInputValue,
    },
  };
}

/** Frontend state consumed by enrichSpec. */
export interface EnrichSpecContext {
  /** Live metric snapshots grouped by owning node id. */
  readonly metricsByNode: ReadonlyMap<string, TaggedMetricSnapshot[]>;
  /** Accumulated metric history per node id, for sparkline charts. */
  readonly metricHistory: ReadonlyMap<string, MetricTimeSeries[]>;
  /** Chart time window in sim-seconds, or null for "all". */
  readonly timeWindow: number | null;
  /** Registered input descriptors keyed by input id. */
  readonly inputDescriptorMap: ReadonlyMap<string, InputDescriptor>;
  /** Frontend-owned current input values keyed by input id. */
  readonly inputValues: Record<string, number | boolean>;
  /** Change handler wired into simInput nodes. */
  readonly setInputValue: (id: string, value: number | boolean) => void;
}

/**
 * Enrich a DiagramSpec's nodes with live simulation data. Node-level
 * fields are augmented; all spec-level fields (edges, groups, appearance)
 * are preserved as-is. Returns the input spec unchanged when there is
 * nothing to enrich.
 */
export function enrichSpec(
  base: DiagramSpec,
  ctx: EnrichSpecContext,
): DiagramSpec {
  const {
    metricsByNode,
    metricHistory,
    timeWindow,
    inputDescriptorMap,
    inputValues,
    setInputValue,
  } = ctx;

  const hasMetrics = metricsByNode.size > 0;
  const hasInputs = inputDescriptorMap.size > 0;
  // Always enrich when the spec contains simInput nodes: the slider is a
  // fully controlled component and needs `onValueChange` (and `value`)
  // wired in on every render, including before the simulation has ever
  // been started. `inputDescriptorMap` is only populated after the first
  // `init()`, so relying on `hasInputs` here would leave pre-run sliders
  // without an onChange handler and React would silently revert edits.
  const hasInputNodes = base.nodes.some((n) => n.type === "simInput");
  if (!hasMetrics && !hasInputs && !hasInputNodes) return base;

  const enrichedNodes = base.nodes.map((node) => {
    let enriched = node;

    const metrics = metricsByNode.get(node.id);
    if (metrics) {
      const history = metricHistory.get(node.id);
      enriched = {
        ...enriched,
        data: { ...enriched.data, metrics, history, timeWindow },
      };
    }

    if (node.type === "simInput") {
      enriched = enrichInputNode(
        enriched,
        inputDescriptorMap.get(node.id),
        inputValues,
        setInputValue,
      );
    }

    if (node.inlineChildren && node.inlineChildren.length > 0 && hasMetrics) {
      const enrichedInline = node.inlineChildren.map((child) => {
        const childMetrics = metricsByNode.get(child.id);
        if (!childMetrics || childMetrics.length === 0) return child;
        const formatted = childMetrics
          .map((m) => formatMetricValue(m.value.value, m.unit))
          .join(" / ");
        return { ...child, value: formatted };
      });
      enriched = { ...enriched, inlineChildren: enrichedInline };
    }

    return enriched;
  });

  return {
    ...base,
    nodes: enrichedNodes,
    edges: base.edges,
    groups: base.groups,
  };
}
